import { Routes, Route, Navigate, useNavigate, useLocation, useParams } from "react-router-dom";
import { Suspense, lazy, useEffect, useRef } from "react";
import type { RefObject } from "react";
import { AuthProvider, useAuth } from "./lib/AuthContext";
import { BrandingProvider } from "./lib/BrandingContext";
import { StatsProvider } from "./lib/StatsContext";
import BottomNav from "./components/BottomNav";

const LoginPage    = lazy(() => import("./pages/LoginPage"));
const HomePage     = lazy(() => import("./pages/HomePage"));
const IdeasPage    = lazy(() => import("./pages/IdeasPage"));
const DiscoverPage = lazy(() => import("./pages/DiscoverPage"));
const CatalogPage  = lazy(() => import("./pages/CatalogPage"));
const SetlistPage  = lazy(() => import("./pages/SetlistPage"));
const SharePage    = lazy(() => import("./pages/SharePage"));
const AdminSettingsPage = lazy(() => import("./pages/AdminSettings"));

function PageLoader() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100%", color: "var(--muted)", fontSize: "0.875rem" }}>
      Loading…
    </div>
  );
}

// Tab order for swipe navigation
const SWIPE_TABS = ['/', '/ideas', '/discover', '/catalog', '/setlist'];

// Horizontal swipe → navigate to adjacent tab.
// Ignores swipes that are mostly vertical, too short, or start inside a lightbox/modal.
function useSwipeNav(scrollRef: React.RefObject<HTMLDivElement>) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const idxRef = useRef(-1);
  idxRef.current = SWIPE_TABS.findIndex(tab =>
    tab === '/' ? pathname === '/' : pathname.startsWith(tab)
  );

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    let startX = 0;
    let startY = 0;
    let tracking = false;

    const onTouchStart = (e: TouchEvent) => {
      // Don't swipe while a modal/lightbox is open
      if ((e.target as Element).closest('.lightbox-backdrop, .lightbox-panel, .perform-mode-overlay')) {
        tracking = false;
        return;
      }
      startX = e.touches[0]!.clientX;
      startY = e.touches[0]!.clientY;
      tracking = true;
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (!tracking) return;
      tracking = false;
      const dx = e.changedTouches[0]!.clientX - startX;
      const dy = e.changedTouches[0]!.clientY - startY;
      // Require a mostly-horizontal swipe of at least 60px
      if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      const idx = idxRef.current;
      if (idx === -1) return;
      if (dx < 0 && idx < SWIPE_TABS.length - 1) navigate(SWIPE_TABS[idx + 1]!);
      else if (dx > 0 && idx > 0) navigate(SWIPE_TABS[idx - 1]!);
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchend', onTouchEnd);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // scrollRef is stable; idxRef tracks current tab without re-attaching
}

// Keep --vvh (visual viewport height) up-to-date so modals can avoid being
// obscured by the iOS on-screen keyboard.
function useVisualViewportHeight() {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      document.documentElement.style.setProperty('--vvh', `${vv.height}px`);
      document.documentElement.style.setProperty('--vv-top', `${vv.offsetTop}px`);
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);
}

// Pull-to-refresh: drag the top of any page downward to reload.
// Blocked automatically when a modal/lightbox or Performance Mode is open.
// Uses direct DOM manipulation (no React state) so the gesture is butter-smooth.
//
// Behaviour mirrors native iOS pull-to-refresh:
//  • Content slides down with your finger (translateY on the scroll container)
//  • An arrow icon appears below the safe-area / Dynamic Island cut-out
//  • Arrow rotates from ↓ → ↑ as you approach the threshold
//  • At threshold the arrow turns bright white ("release to refresh")
//  • On release past threshold: content snaps to a hold position, arrow
//    becomes a spinner, then the page reloads after a brief moment
//  • On release before threshold: everything animates back to normal
function usePullToRefresh(
  scrollRef: RefObject<HTMLDivElement>,
  indicatorRef: RefObject<HTMLDivElement>,
) {
  useEffect(() => {
    const el        = scrollRef.current;
    const indicator = indicatorRef.current;
    if (!el || !indicator) return;

    // MAX_H is the strip height when fully pulled.  88 px puts the icon
    // (anchored 12 px from the bottom) at ~76 px from the top — safely below
    // the iPhone 14 Dynamic Island (≈ 59 px safe-area inset).
    // THRESHOLD is intentionally higher than MAX_H so the user must pull past
    // the fully-extended indicator before it triggers — requires a deliberate gesture.
    const THRESHOLD = 130;
    const MAX_H     = 88;

    let startY  = 0;
    let pulling = false;

    const isBlocked = () => {
      if (document.querySelector('.perform-mode-overlay')) return true;
      if (document.querySelector('.lightbox-backdrop'))    return true;
      // Don't PTR while the user is actively typing in any field
      const ae = document.activeElement as HTMLElement | null;
      if (ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.tagName === 'SELECT' || ae.isContentEditable)) return true;
      return false;
    };

    const arrow   = indicator.querySelector<HTMLElement>('.ptr-arrow');
    const spinner = indicator.querySelector<HTMLElement>('.ptr-spinner');

    // Grow/shrink the indicator strip. Content stays put — no translateY —
    // so iOS's native PTR can't create a conflicting second animation.
    const setProgress = (h: number, animated: boolean) => {
      const ease = animated ? '0.3s cubic-bezier(0.25, 1, 0.5, 1)' : 'none';
      indicator.style.transition = animated ? `height ${ease}` : 'none';
      indicator.style.height     = `${Math.max(0, h)}px`;

      // Rotate the arrow: 0 % → pointing down, 100 % → pointing up
      const pct = Math.min(h / MAX_H, 1);
      if (arrow) {
        arrow.style.transform   = `rotate(${Math.round(pct * 180)}deg)`;
        arrow.style.color       = pct >= 1 ? 'var(--ink, #f1f2f4)' : 'var(--muted, #6b7280)';
        arrow.style.borderColor = pct >= 1 ? 'var(--ink, #f1f2f4)' : 'var(--line, #2a2a2e)';
      }
    };

    const showSpinner = () => {
      if (arrow)   arrow.style.display   = 'none';
      if (spinner) spinner.style.display = 'flex';
    };

    const reset = (animated: boolean) => {
      if (arrow)   { arrow.style.display = 'flex'; arrow.style.transform = 'rotate(0deg)'; arrow.style.color = 'var(--muted, #6b7280)'; arrow.style.borderColor = 'var(--line, #2a2a2e)'; }
      if (spinner) spinner.style.display = 'none';
      setProgress(0, animated);
    };

    const onTouchStart = (e: TouchEvent) => {
      if (isBlocked())      return;
      if (el.scrollTop > 2) return; // only activate when page is scrolled to top
      startY  = e.touches[0]!.clientY;
      pulling = true;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!pulling) return;
      if (isBlocked()) { pulling = false; reset(false); return; }
      const dy = e.touches[0]!.clientY - startY;
      if (dy <= 0) { setProgress(0, false); return; }
      if (dy > 8) e.preventDefault(); // block browser's native overscroll/PTR only once clearly pulling
      // Sqrt damping = natural rubber-band resistance
      const h = Math.min(Math.sqrt(dy / THRESHOLD) * MAX_H, MAX_H * 1.08);
      setProgress(h, false);
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (!pulling) return;
      pulling = false;
      if (isBlocked()) { reset(false); return; }
      const dy = e.changedTouches[0]!.clientY - startY;
      if (dy >= THRESHOLD) {
        // Snap to hold position → show spinner → reload
        setProgress(MAX_H, true);
        showSpinner();
        setTimeout(() => window.location.reload(), 700);
      } else {
        reset(true);
      }
    };

    const onCancel = () => { if (pulling) { pulling = false; reset(true); } };

    // Listen at document level so we intercept touches on any child element
    // before the browser's native PTR can activate on them.
    document.addEventListener('touchstart',  onTouchStart, { passive: true });
    document.addEventListener('touchmove',   onTouchMove,  { passive: false });
    document.addEventListener('touchend',    onTouchEnd,   { passive: true });
    document.addEventListener('touchcancel', onCancel,     { passive: true });
    return () => {
      document.removeEventListener('touchstart',  onTouchStart);
      document.removeEventListener('touchmove',   onTouchMove);
      document.removeEventListener('touchend',    onTouchEnd);
      document.removeEventListener('touchcancel', onCancel);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

function LearningRedirect() {
  const { songId } = useParams();
  return <Navigate to={`/catalog/${songId ?? ""}`} replace />;
}

// On a wide screen the app is a centered column; the page itself doesn't
// scroll, .app-scroll does. A wheel over the dark margins outside the column
// would otherwise do nothing, so send it to the column.
function useWheelOutsideFrame(scrollRef: RefObject<HTMLDivElement>) {
  useEffect(() => {
    const onWheel = (e: WheelEvent) => {
      const root = document.getElementById("root");
      const scroller = scrollRef.current;
      if (!scroller || !root || root.contains(e.target as Node)) return;
      scroller.scrollTop += e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
    };
    window.addEventListener("wheel", onWheel, { passive: true });
    return () => window.removeEventListener("wheel", onWheel);
  }, [scrollRef]);
}

// AppShell: flex column that fills #root. .app-scroll holds page content and
// scrolls; BottomNav sits as a real element at the very bottom of the column.
// This avoids position:fixed which misbehaves in DuckDuckGo "Open as Web App".
function AppShell({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();
  const scrollRef    = useRef<HTMLDivElement>(null);
  const indicatorRef = useRef<HTMLDivElement>(null);
  useSwipeNav(scrollRef);
  useWheelOutsideFrame(scrollRef);
  useVisualViewportHeight();
  usePullToRefresh(scrollRef, indicatorRef);


  return (
    <>
      {/*
        Pull-to-refresh indicator strip.
        Height is driven directly by the hook (no React state).
        The icon is absolutely pinned to the bottom of the strip so it always
        appears below the Dynamic Island / notch regardless of strip height.
      */}
      <div
        ref={indicatorRef}
        style={{
          position:      'fixed',
          top:           0,
          left:          0,
          right:         0,
          zIndex:        9000,
          height:        0,
          overflow:      'hidden',
          background:    'var(--bg, #0b0b0c)',
          pointerEvents: 'none',
        }}
      >
        {/* Icon container pinned 12 px from the bottom of the strip */}
        <div style={{
          position:       'absolute',
          bottom:         12,
          left:           0,
          right:          0,
          display:        'flex',
          justifyContent: 'center',
        }}>
          {/* Arrow — visible while the user is dragging */}
          <div
            className="ptr-arrow"
            style={{
              display:         'flex',
              alignItems:      'center',
              justifyContent:  'center',
              width:           28,
              height:          28,
              borderRadius:    '50%',
              border:          '2px solid var(--line, #2a2a2e)',
              color:           'var(--muted, #6b7280)',
              fontSize:        14,
              lineHeight:      1,
              transformOrigin: 'center',
            }}
          >
            ↓
          </div>

          {/* Spinner — swapped in once the user releases past the threshold */}
          <div
            className="ptr-spinner"
            style={{
              display:        'none',
              width:          26,
              height:         26,
              borderRadius:   '50%',
              border:         '2.5px solid var(--line, #2a2a2e)',
              borderTopColor: 'var(--ink, #f1f2f4)',
              animation:      'ptr-spin 0.75s linear infinite',
            }}
          />
        </div>
      </div>

      <div className="app-scroll" ref={scrollRef}>{children}</div>
      {user && !location.pathname.startsWith("/s/") && <BottomNav />}
    </>
  );
}

// Prefetch all page bundles in the background after initial load so
// first navigation to each page feels instant instead of waiting for a fetch.
function usePrefetchPages() {
  useEffect(() => {
    const t = setTimeout(() => {
      import("./pages/IdeasPage");
      import("./pages/DiscoverPage");
      import("./pages/CatalogPage");
      import("./pages/SetlistPage");
    }, 1500);
    return () => clearTimeout(t);
  }, []);
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  if (isLoading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function AppRoutes() {
  const { user, isLoading } = useAuth();
  if (isLoading) return <PageLoader />;

  return (
    <AppShell>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
          <Route path="/" element={<RequireAuth><HomePage /></RequireAuth>} />
          <Route path="/ideas" element={<RequireAuth><IdeasPage /></RequireAuth>} />
          <Route path="/discover" element={<RequireAuth><DiscoverPage /></RequireAuth>} />
          {/* Learn is hidden (2026-10-24): old links land in the catalog. The
              page itself (pages/LearningPage.tsx) is kept, just unrouted. */}
          <Route path="/learning" element={<Navigate to="/catalog" replace />} />
          <Route path="/learning/:songId" element={<LearningRedirect />} />
          <Route path="/catalog" element={<RequireAuth><CatalogPage /></RequireAuth>} />
          <Route path="/catalog/:songId" element={<RequireAuth><CatalogPage /></RequireAuth>} />
          <Route path="/setlist" element={<RequireAuth><SetlistPage /></RequireAuth>} />
          <Route path="/setlist/:setlistId" element={<RequireAuth><SetlistPage /></RequireAuth>} />
          <Route path="/admin/settings" element={<RequireAuth><AdminSettingsPage /></RequireAuth>} />
          {/* Public share page — no auth, link is the credential. */}
          <Route path="/s/:shortId" element={<SharePage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </AppShell>
  );
}

export default function App() {
  usePrefetchPages();
  return (
    <AuthProvider>
      <BrandingProvider>
        <StatsProvider>
          <AppRoutes />
        </StatsProvider>
      </BrandingProvider>
    </AuthProvider>
  );
}
